import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LexiconModule } from '@galaxy/lexicon';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { GalaxyPopoverModule } from '@vendasta/galaxy/popover';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { VaIconModule, VaStencilsModule } from '@vendasta/uikit';
import { FilterModule } from '@vendasta/va-filter2';
import { Filter2TableModule, TABLE_DEFINITION, VaFilteredMatTableService } from '@vendasta/va-filter2-table';
import { ClickOutsideModule } from 'ng-click-outside';
import { NgxJsonViewerModule } from 'ngx-json-viewer';
import en from '../assets/i18n/en_devel.json';
import { ColorIconComponent } from '../color-icon/color-icon.component';
import { DisconnectServiceModalComponent } from '../disconnect-service-modal/disconnect-service-modal.component';
import { RepcoreApiService } from '../repcore-api/repcore-api.service';
import { VApiService } from '../repcore-api/vapi.service';
import { UpgradeCTADialogService } from '../upgrade-cta-dialog/upgrade-cta-dialog.service';
import { ConnectedAccountModalComponent } from './connected-account-modal/connected-account-modal.component';
import { ListingScoreSummaryCardComponent } from './listing-score-summary-card/listing-score-summary-card.component';
import { AccuracyTextPipe } from './listing-sync-table/columns/presence-accuracy/accuracy-text.pipe';
import { AnchorDataKeyToIconPipe } from './listing-sync-table/columns/presence-accuracy/errors-modal/anchor-data-key-to-icon.pipe';
import { ErrorsModalComponent } from './listing-sync-table/columns/presence-accuracy/errors-modal/errors-modal.component';
import { FillEmptyAnchorDataPipe } from './listing-sync-table/columns/presence-accuracy/errors-modal/fill-empty-anchor-data.pipe';
import { PresenceAccuracyComponent } from './listing-sync-table/columns/presence-accuracy/presence-accuracy.component';
import { IconComponent } from './listing-sync-table/columns/source-name-column/icon/icon.component';
import { SourceNameColumnComponent } from './listing-sync-table/columns/source-name-column/source-name-column.component';
import { StatusColumnComponent } from './listing-sync-table/columns/status-column/status-column.component';
import { ListingSidebarComponent } from './listing-sync-table/listing-sidebar/listing-sidebar.component';
import { ListingSyncTableFilterService } from './listing-sync-table/listing-sync-table-filter.service';
import { tableDefinitionFactory } from './listing-sync-table/listing-sync-table-model';
import { ListingSyncTableComponent } from './listing-sync-table/listing-sync-table.component';
import { ListingSyncTableService } from './listing-sync-table/listing-sync-table.service';
import { SyncInfoModalComponent } from './listing-sync-table/sync-info-modal/sync-info-modal.component';
import { ListingSyncService } from './listing-sync.service';
import { ListingsManagementComponent } from './listings-management.component';
import { ListingsSummaryCardComponent } from './listings-summary-card/listings-summary-card.component';
import { PrimarySourceComponent } from './primary-sources/primary-source/primary-source.component';
import { PrimarySourcesComponent } from './primary-sources/primary-sources.component';
import { SyncingProgressSummaryCardComponent } from './syncing-progress-summary-card/syncing-progress-summary-card.component';
import { ListingsNapMapCardComponent } from './listings-nap-map-card/listings-nap-map-card.component';
import { GoogleMap, MapMarker } from '@angular/google-maps';
import EditBusinessProfileComponent from './edit-business-profile/edit-business-profile.component';
import { ListingSidebarAccuracyComponent } from './listing-sync-table/listing-sidebar-accuracy/listing-sidebar-accuracy.component';
import { LanguageService } from './language-service.service';
import { ListingSidebarActionsComponent } from './listing-sync-table/listing-sidebar-actions/listing-sidebar-actions.component';
import { ListingSidebarErrorsComponent } from './listing-sync-table/listing-sidebar-errors/listing-sidebar-errors.component';
import { ListingSidebarAccountsComponent } from './listing-sync-table/listing-sidebar-accounts/listing-sidebar-accounts.component';
import { ConnectionButtonComponent } from '@galaxy/platform-integrations';

@NgModule({
  declarations: [
    ListingsManagementComponent,
    ListingSidebarComponent,
    SyncInfoModalComponent,
    ConnectedAccountModalComponent,
    ListingSyncTableComponent,
    SourceNameColumnComponent,
    IconComponent,
    StatusColumnComponent,
    ListingsSummaryCardComponent,
    ListingScoreSummaryCardComponent,
    SyncingProgressSummaryCardComponent,
    PresenceAccuracyComponent,
    ErrorsModalComponent,
    AccuracyTextPipe,
    AnchorDataKeyToIconPipe,
    FillEmptyAnchorDataPipe,
    PrimarySourcesComponent,
    PrimarySourceComponent,
    DisconnectServiceModalComponent,
    ColorIconComponent,
    EditBusinessProfileComponent,
    ListingsNapMapCardComponent,
    ListingsNapMapCardComponent,
    EditBusinessProfileComponent,
    ListingSidebarAccuracyComponent,
    ListingSidebarActionsComponent,
    ListingSidebarErrorsComponent,
    ListingSidebarAccountsComponent,
  ],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatDialogModule,
    MatMenuModule,
    MatIconModule,
    GalaxyTooltipModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatTableModule,
    MatRadioModule,
    TranslateModule,
    VaIconModule,
    Filter2TableModule,
    ReactiveFormsModule,
    FilterModule,
    MatTooltipModule,
    GalaxyFormFieldModule,
    GalaxyPageModule,
    NgxJsonViewerModule,
    MatExpansionModule,
    MatListModule,
    GalaxyBadgeModule,
    GalaxyLoadingSpinnerModule,
    MatSidenavModule,
    ClickOutsideModule,
    GalaxyPopoverModule,
    ReactiveFormsModule,
    FormsModule,
    MatCheckboxModule,
    LexiconModule.forChild({
      componentName: 'common/local-seo',
      baseTranslation: en,
    }),
    MatExpansionModule,
    GoogleMap,
    MapMarker,
    ConnectionButtonComponent,
    VaStencilsModule,
  ],
  exports: [
    ListingsSummaryCardComponent,
    ListingScoreSummaryCardComponent,
    SyncingProgressSummaryCardComponent,
    ListingSyncTableComponent,
    PrimarySourcesComponent,
    ListingsManagementComponent,
    ListingsNapMapCardComponent,
    EditBusinessProfileComponent,
    IconComponent,
  ],
  providers: [
    VaFilteredMatTableService,
    ListingSyncTableFilterService,
    {
      provide: TABLE_DEFINITION,
      useFactory: tableDefinitionFactory,
      deps: [ListingSyncTableFilterService],
    },
    RepcoreApiService,
    VApiService,
    UpgradeCTADialogService,
    ListingSyncService,
    ListingSyncTableService,
    LanguageService,
  ],
})
export class LocalSEOListingsManagementModule {}
